<template>
  <div class="w-100 chart-wrapper">
    <fusioncharts
      v-if="tempGraph"
      width="100%"
      :height="height"
      type="zoomline"
      dataFormat="json"
      :dataSource="dataSourceTemp"
    ></fusioncharts>
    <fusioncharts
      v-if="humiGraph"
      width="100%"
      :height="height"
      type="zoomline"
      dataFormat="json"
      :dataSource="dataSourceHumi"
    ></fusioncharts>
  </div>
</template>

<script>
import moment from "moment";
import Vue from "vue";
import VueFusionCharts from "vue-fusioncharts";
import FusionCharts from "fusioncharts";
import zoomline from "fusioncharts/fusioncharts.zoomline";
import FusionTheme from "fusioncharts/themes/fusioncharts.theme.fusion";
// FusionCharts.options.license({
//   key: process.env.VUE_APP_FUSION_LICENSE_KEY,
//   creditLabel: false,
// });
Vue.use(VueFusionCharts, FusionCharts, zoomline, FusionTheme);

export default {
  name: "CombinedChart",
  props: {
    stats: Array,
    tempGraph: Boolean,
    humiGraph: Boolean,
    title: {
      default: "Chart",
      type: String,
    },
    height: {
      default: "470",
      type: String,
    },
    baseFontSize: String,
    outCnvBaseFontSize: String,
    captionFontSize: String,
    subcaptionFontSize: String,
    xAxisNameFontSize: String,
    yAxisNameFontSize: String,
    legendIconScale: String,
    legendItemFontSize: String,
    hasTemp2: Boolean,
  },
  data() {
    return {
      type: "zoomline",
      dataformat: "json",
      dataSourceTemp: "",
      dataSourceHumi: "",
      labels: [],
      temp: [],
      maxTemp: [],
      minTemp: [],
      humi: [],
      minHumi: [],
      maxHumi: [],
      light: [],
      temp2: [],
    };
  },
  computed: {
    chartToRender() {
      return this.tempGraph || this.humiGraph;
    },
  },
  mounted() {
    this.initialiseChart();
  },
  watch: {
    stats: {
      handler: function () {
        this.initialiseChart();
      },

      deep: true,
      immediate: true,
    },
    hasTemp2() {
      this.initialiseChart();
    },
  },
  methods: {
    initialiseChart() {
      this.labels = [];
      this.temp = [];
      this.minTemp = [];
      this.maxTemp = [];
      this.humi = [];
      this.minHumi = [];
      this.maxHumi = [];
      this.light = [];
      this.temp2 = [];
      if (this.stats) {
        for (const element of this.stats) {
          if (element.temperature === "" || element.humidity === 0) {
            continue; // Skip this iteration and move to the next one
          }
          let date = element.deviceTimeStamp;
          // if (typeof date === "string") {
          //   date = Number(date);
          // }
          date = moment(date).format("DD/MM/YY | HH:mm");
          this.labels.push({
            label: date,
          });
          this.temp.push({
            value: element.temperature,
          });
          this.maxTemp.push({
            value: element.deviceSettings.maxTemperature,
          });
          this.minTemp.push({
            value: element.deviceSettings.minTemperature,
          });
          this.humi.push({
            value: element.humidity,
          });
          this.maxHumi.push({
            value: element.deviceSettings.maxHumidity,
          });
          this.minHumi.push({
            value: element.deviceSettings.minHumidity,
          });

          this.light.push({
            value: element.light,
          });
          if (this.hasTemp2) {
            this.temp2.push({
              value: element.temperature2,
            });
          }
        }
      }

      if (this.labels.length > 0) {
        const temperatureDataset = [
          {
            seriesName: this.$t("min-temp"),
            data: this.minTemp,
            color: "#F3C963",
          },
          {
            seriesName: this.$t("amb-temp"),
            data: this.temp,
            color: "#28A84A",
          },
          {
            seriesName: this.$t("max-temp"),
            data: this.maxTemp,
            color: "#DC3030",
          },
        ];

        if (this.hasTemp2) {
          temperatureDataset.splice(2, 0, {
            seriesName: "Temperature (Amb)",
            data: this.temp2,
            color: "#F44336",
            anchorBgColor: "#F44336",
            anchorBorderColor: "#F44336",
            plottooltext: "Temperature - Ambient: $dataValue °C",
          });
        }

        this.dataSourceTemp = {
          chart: {
            caption: this.$t("caption-temp"),
            subcaption: this.$t("sub_cap"),
            yaxisname: this.$t("temp"),
            numbersuffix: "°C",
            xaxisname: this.$t("time"),
            lineThickness: "3",
            theme: "fusion",
            paletteColors: "#F3C963,#28A84A,#F44336,#DC3030",
            exportEnabled: "1",
            exportFileName: this.title,
            rotateLabels: "0",
            labelDisplay: "rotate",
            baseFontSize: this.baseFontSize,
            outCnvBaseFontSize: this.outCnvBaseFontSize,
            captionFontSize: this.captionFontSize,
            subcaptionFontSize: this.subcaptionFontSize,
            yAxisNameFontSize: this.yAxisNameFontSize,
            xAxisNameFontSize: this.xAxisNameFontSize,
            legendIconScale: this.legendIconScale,
            legendItemFontSize: this.legendItemFontSize,
            zoomType: "xy",
            zoomEnabled: "1",
          },
          categories: [
            {
              category: this.labels,
            },
          ],
          dataset: temperatureDataset,
        };
        this.dataSourceHumi = {
          chart: {
            caption: this.$t("caption-humid"),
            subcaption: this.$t("sub_cap"),
            yaxisname: this.$t("humid"),
            numbersuffix: "%",
            xaxisname: this.$t("time"),
            lineThickness: "3",
            theme: "fusion",
            paletteColors: "#F3C963,#28A84A,#DC3030",
            exportEnabled: "1",
            exportFileName: this.title,
            baseFontSize: this.baseFontSize,
            outCnvBaseFontSize: this.outCnvBaseFontSize,
            captionFontSize: this.captionFontSize,
            subcaptionFontSize: this.subcaptionFontSize,
            yAxisNameFontSize: this.yAxisNameFontSize,
            xAxisNameFontSize: this.xAxisNameFontSize,
            legendIconScale: this.legendIconScale,
            legendItemFontSize: this.legendItemFontSize,
          },
          categories: [
            {
              category: this.labels,
            },
          ],
          dataset: [
            {
              seriesName: this.$t("min-humid"),
              data: this.minHumi,
            },
            {
              seriesName: this.$t("amb-humid"),
              data: this.humi,
            },
            {
              seriesName: this.$t("max-humid"),
              data: this.maxHumi,
            },
          ],
        };
      } else {
        this.dataSourceTemp = this.dataSourceHumi = "";
      }
    },
  },
};
</script>

<style scoped>
.chart-wrapper {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow-x: auto;
}
</style>
